import React, { useEffect, useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import type { paths } from '../../api/types';
import { Button } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import type { TranslationKey } from '../../utils/translations';

type Preview = paths['/api/v1/plugin/consent/preview']['post']['responses'][200]['content']['application/json'];
type ConsentState = 'loading' | 'ready' | 'submitting' | 'expired' | 'error' | 'complete';
const permissionKeys: Record<string, TranslationKey> = {
  'priceloss:read': 'plugin.read',
  'priceloss:write': 'plugin.write',
  'priceloss:events': 'plugin.events',
};
const failureState = (status: number): ConsentState =>
  status === 400 || status === 410 ? 'expired' : 'error';

async function loadPreview(requestToken: string, signal: AbortSignal) {
  try {
    const { data, error, response } = await apiClient.POST('/api/v1/plugin/consent/preview', {
      body: { request_token: requestToken }, signal,
      cache: 'no-store', credentials: 'omit', redirect: 'error',
    });
    if (error || !data) return { preview: null, state: failureState(response.status) };
    if (!Number.isFinite(Date.parse(data.expires_at)) || Date.parse(data.expires_at) <= Date.now()) {
      return { preview: null, state: 'expired' as const };
    }
    if (data.scopes.some((scope) => !Object.hasOwn(permissionKeys, scope))) {
      return { preview: null, state: 'error' as const };
    }
    return { preview: data, state: 'ready' as const };
  } catch {
    return { preview: null, state: 'error' as const };
  }
}

async function submitDecision(requestToken: string, approved: boolean, signal: AbortSignal): Promise<ConsentState> {
  try {
    const { data, error, response } = await apiClient.POST('/api/v1/plugin/consent', {
      body: { request_token: requestToken, approved }, signal,
      cache: 'no-store', credentials: 'omit', redirect: 'error',
    });
    if (signal.aborted) return 'complete';
    if (error || !data) return failureState(response.status);
    const target = new URL(data.redirect_uri);
    if (target.protocol !== 'https:' || target.username || target.password) return 'error';
    window.history.replaceState(window.history.state, '', window.location.pathname);
    window.location.assign(data.redirect_uri);
    return 'complete';
  } catch {
    return 'error';
  }
}

function useConnectionConsent(requestToken: string) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [state, setState] = useState<ConsentState>('loading');
  const request = useRef<AbortController | null>(null);
  const submitting = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    request.current = controller;
    void loadPreview(requestToken, controller.signal).then((result) => {
      if (!controller.signal.aborted) { setPreview(result.preview); setState(result.state); }
    });
    return () => { request.current?.abort(); controller.abort(); };
  }, [requestToken]);
  useEffect(() => {
    if (!preview) return;
    const delay = Math.min(Date.parse(preview.expires_at) - Date.now(), 2147483647);
    const timer = window.setTimeout(() => {
      request.current?.abort();
      setState('expired');
    }, Math.max(0, delay));
    return () => window.clearTimeout(timer);
  }, [preview]);
  const decide = async (approved: boolean): Promise<void> => {
    if (!preview || submitting.current || state !== 'ready') return;
    if (Date.parse(preview.expires_at) <= Date.now()) { setState('expired'); return; }
    submitting.current = true;
    setState('submitting');
    const controller = new AbortController();
    request.current = controller;
    const result = await submitDecision(requestToken, approved, controller.signal);
    if (!controller.signal.aborted) setState(result);
  };
  return { preview, state, decide };
}

const ConsentDetails: React.FC<{ preview: Preview }> = ({ preview }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  return (
    <>
      <div className="space-y-2">
        <p className="text-sm text-slate-700 dark:text-slate-300">{t('plugin.client')}</p>
        <p className="font-semibold break-words"><bdi>{preview.display_name}</bdi></p>
        <p className="text-sm text-slate-700 dark:text-slate-300">{t('plugin.account')}</p>
        <p className="text-sm break-words"><bdi>{user?.email}</bdi></p>
      </div>
      <div>
        <h2 className="font-semibold mb-3">{t('plugin.permissions')}</h2>
        <ul className="list-disc ps-5 space-y-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
          {preview.scopes.map((scope) => <li key={scope}>{t(permissionKeys[scope]!)}</li>)}
        </ul>
      </div>
    </>
  );
};

export const ConnectionConsent: React.FC<{ requestToken: string }> = ({ requestToken }) => {
  const { t } = useLanguage();
  const { preview, state, decide } = useConnectionConsent(requestToken);
  if (state === 'loading') return <p role="status">{t('plugin.loading')}</p>;
  if (state === 'expired' || state === 'error') {
    return <p role="alert">{t(state === 'expired' ? 'plugin.expired' : 'plugin.error')}</p>;
  }
  if (state === 'complete') return <p role="status">{t('plugin.complete')}</p>;
  if (!preview) return null;
  return (
    <div className="space-y-6">
      <ConsentDetails preview={preview} />
      <p className="text-sm text-slate-700 dark:text-slate-300">{t('plugin.notice')}</p>
      {state === 'submitting' && <p role="status">{t('plugin.submitting')}</p>}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button className="flex-1 min-h-11 bg-emerald-700 hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-800" disabled={state === 'submitting'} onClick={() => void decide(true)}>
          {t('plugin.approve')}
        </Button>
        <Button className="flex-1 min-h-11" variant="outline" disabled={state === 'submitting'} onClick={() => void decide(false)}>
          {t('plugin.deny')}
        </Button>
      </div>
    </div>
  );
};
